import { useState, useCallback, useMemo } from 'react';
import { SbNotification } from 'uiCore';
import { getRoleReportingView, updateRoleReporting } from 'Api/apiService';
import { ROLE_REPORTING_ERROR_CODES as ERR, ROLE_REPORTING_MESSAGES as MSG } from 'constants/roleReporting.constants';

/**
 * Maps a backend error_code/status to a notification. Returns true if handled.
 */
const handleRoleReportingError = (error) => {
  const errCode = error?.response?.data?.error_code;
  const backendMsg = error?.response?.data?.message || '';
  const status = error?.response?.status;

  if (errCode === ERR.APEX_WITH_MANAGER) {
    SbNotification.error(MSG.ERR_APEX_WITH_MANAGER, backendMsg);
    return true;
  }
  if (errCode === ERR.SELF_REPORTING) {
    SbNotification.error(MSG.ERR_SELF_REPORTING, backendMsg);
    return true;
  }
  if (errCode === ERR.REPORTING_CYCLE) {
    SbNotification.error(MSG.ERR_REPORTING_CYCLE, backendMsg);
    return true;
  }
  if (status === 404 || errCode === ERR.JOB_DESC_NOT_FOUND) {
    SbNotification.error(MSG.ERR_NOT_FOUND, backendMsg || MSG.ERR_NOT_FOUND_DESC);
    return true;
  }
  if (status >= 500 || errCode === ERR.QUERY_FAILED || errCode === ERR.UPDATE_FAILED || errCode === ERR.INTERNAL_ERROR) {
    SbNotification.error(MSG.ERR_SERVER, backendMsg || MSG.ERR_SERVER_DESC);
    return true;
  }
  if (backendMsg) {
    SbNotification.error(MSG.SAVE_FAILED, backendMsg);
    return true;
  }
  return false;
};

const useRoleReporting = (email) => {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Inline edit state
  const [editingRowId, setEditingRowId] = useState(null);
  const [editValues, setEditValues] = useState({ managerId: null, isApexRole: false });
  const [savingRowId, setSavingRowId] = useState(null);

  const fetchData = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    try {
      const response = await getRoleReportingView(email);
      setAllData(Array.isArray(response) ? response : []);
    } catch {
      SbNotification.error(MSG.LOAD_FAILED, MSG.LOAD_FAILED_DESC);
      setAllData([]);
    } finally {
      setLoading(false);
    }
  }, [email]);

  // Manager options for the picker — every other active JD, excluding the row being edited
  const managerOptionsFor = useCallback(
    (sbUniqueId) =>
      allData
        .filter((row) => row.sb_unique_id !== sbUniqueId)
        .map((row) => ({
          value: row.sb_unique_id,
          label: `${row.job_title}${row.job_code ? ` (${row.job_code})` : ''}`,
        })),
    [allData],
  );

  const handleEditStart = useCallback((record) => {
    setEditingRowId(record.sb_unique_id);
    setEditValues({
      managerId: record.role_reporting_to?.sb_unique_id ?? null,
      isApexRole: !!record.is_apex_role,
    });
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditingRowId(null);
    setEditValues({ managerId: null, isApexRole: false });
  }, []);

  // Selecting a manager clears Apex (a role with a manager cannot be apex)
  const handleManagerChange = useCallback((managerId) => {
    setEditValues({ managerId: managerId ?? null, isApexRole: false });
  }, []);

  // Toggling Apex on clears the selected manager (an apex role cannot have a manager)
  const handleApexChange = useCallback((checked) => {
    setEditValues((prev) => ({ managerId: checked ? null : prev.managerId, isApexRole: checked }));
  }, []);

  const handleEditSave = useCallback(
    async (record) => {
      if (!record?.sb_unique_id) return;

      const payload = editValues.isApexRole
        ? { is_apex_role: true }
        : { manager_sb_unique_id: editValues.managerId, is_apex_role: false };

      setSavingRowId(record.sb_unique_id);
      try {
        const result = await updateRoleReporting(record.sb_unique_id, payload);
        setAllData((prev) =>
          prev.map((row) =>
            row.sb_unique_id === record.sb_unique_id
              ? { ...row, role_reporting_to: result?.role_reporting_to ?? null, is_apex_role: !!result?.is_apex_role }
              : row,
          ),
        );
        SbNotification.success(MSG.SAVE_SUCCESS);
        setEditingRowId(null);
        setEditValues({ managerId: null, isApexRole: false });
      } catch (error) {
        if (!handleRoleReportingError(error)) {
          SbNotification.error(MSG.SAVE_FAILED, MSG.SAVE_FAILED_DESC);
        }
      } finally {
        setSavingRowId(null);
      }
    },
    [editValues],
  );

  const filterOptions = useMemo(() => {
    const buildFilters = (key) =>
      [...new Set(allData.map((row) => row[key]).filter(Boolean))].map((v) => ({ text: v, value: v }));
    return {
      business_unit: buildFilters('business_unit'),
      function: buildFilters('function'),
      job_level: buildFilters('job_level'),
      jd_status: buildFilters('jd_status'),
    };
  }, [allData]);

  return {
    allData,
    loading,
    fetchData,
    filterOptions,
    managerOptionsFor,
    editingRowId,
    editValues,
    savingRowId,
    handleEditStart,
    handleEditCancel,
    handleManagerChange,
    handleApexChange,
    handleEditSave,
  };
};

export default useRoleReporting;
